import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const colors = [
  { label: "Ash Rose", hex: "#d4a0a0" },
  { label: "Beige",    hex: "#c8b89a" },
  { label: "Black",    hex: "#000000" },
  { label: "Blue",     hex: "#1a3a6e" },
  { label: "Cream",    hex: "#e8e0d0" },
  { label: "Gray",     hex: "#888888" },
  { label: "Pink",     hex: "#e05070" },
  { label: "Red",      hex: "#cc2222" },
  { label: "White",    hex: "#eeeeee" },
];

const sizes = ["XS", "S", "M", "L", "XL", "XXL"];

const sectionStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  py: 2.5,
  cursor: "pointer",
  userSelect: "none",
};

const labelStyle = {
  fontSize: "14px",
  letterSpacing: "1.5px",
  fontWeight: 600,
  color: "#000",
};

const FilterDrawer = ({ open, mode = "filter", onClose }) => {
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSize, setSelectedSize] = useState("");
  const [availability, setAvailability] = useState("");
  const [sortBy, setSortBy] = useState("featured");

  const [openSections, setOpenSections] = useState({
    color: false,
    size: false,
    availability: false,
    sortBy: false,
  });

  useEffect(() => {
    if (open && mode === "sort") {
      setOpenSections((prev) => ({
        ...prev,
        sortBy: true,
      }));
    }
  }, [open, mode]);

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleColor = (label) =>
    setSelectedColors((prev) =>
      prev.includes(label) ? prev.filter((c) => c !== label) : [...prev, label]
    );

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: "500px" },
          backgroundColor: "#fff",
          color: "#000",
          display: "flex",
          flexDirection: "column",
        },
      }}
      sx={{ zIndex: 1302 }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, pt: 3, pb: 2 }}>
        <Typography sx={{ fontSize: "16px", letterSpacing: "2px", fontWeight: 700 }}>FILTER</Typography>
        <IconButton onClick={onClose} sx={{ color: "#000", p: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      {/* Scrollable Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3 }}>

        {/* COLOR */}
        <Box sx={sectionStyle} onClick={() => toggleSection("color")}>
          <Typography sx={labelStyle}>COLOR</Typography>
          <KeyboardArrowUpIcon sx={{ fontSize: "18px", transform: openSections.color ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.2s" }} />
        </Box>
        <Collapse in={openSections.color}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 1.5, columnGap: 2, pb: 3 }}>
            {colors.map((c) => (
              <Box
                key={c.label}
                onClick={() => toggleColor(c.label)}
                sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    backgroundColor: c.hex,
                    border: selectedColors.includes(c.label) ? "2px solid #000" : "1.5px solid #bbb",
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: "15px", color: selectedColors.includes(c.label) ? "#000" : "#333" }}>
                  {c.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
        <Divider />

        {/* SIZE */}
        <Box sx={sectionStyle} onClick={() => toggleSection("size")}>
          <Typography sx={labelStyle}>SIZE</Typography>
          <KeyboardArrowUpIcon sx={{ fontSize: "18px", transform: openSections.size ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.2s" }} />
        </Box>
        <Collapse in={openSections.size}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", pb: 2 }}>
            {sizes.map((s) => (
              <FormControlLabel
                key={s}
                control={
                  <Radio
                    checked={selectedSize === s}
                    onChange={() => setSelectedSize(s)}
                    size="small"
                    sx={{ color: "#999", "&.Mui-checked": { color: "#000" } }}
                  />
                }
                label={<Typography sx={{ fontSize: "15px" }}>{s}</Typography>}
              />
            ))}
          </Box>
        </Collapse>
        <Divider />

        {/* AVAILABILITY */}
        <Box sx={sectionStyle} onClick={() => toggleSection("availability")}>
          <Typography sx={labelStyle}>AVAILABILITY</Typography>
          <KeyboardArrowUpIcon sx={{ fontSize: "18px", transform: openSections.availability ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.2s" }} />
        </Box>
        <Collapse in={openSections.availability}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", pb: 2 }}>
            {["In stock", "Out of stock"].map((opt) => (
              <FormControlLabel
                key={opt}
                control={
                  <Radio
                    checked={availability === opt}
                    onChange={() => setAvailability(opt)}
                    size="small"
                    sx={{ color: "#999", "&.Mui-checked": { color: "#000" } }}
                  />
                }
                label={<Typography sx={{ fontSize: "15px" }}>{opt}</Typography>}
              />
            ))}
          </Box>
        </Collapse>
        <Divider />

        {/* SORT BY */}
        <Box sx={sectionStyle} onClick={() => toggleSection("sortBy")}>
          <Typography sx={labelStyle}>SORT BY</Typography>
          <KeyboardArrowUpIcon sx={{ fontSize: "18px", transform: openSections.sortBy ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.2s" }} />
        </Box>
        <Collapse in={openSections.sortBy}>
          <FormControl component="fieldset" sx={{ pb: 2, width: "100%" }}>
            <RadioGroup value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                {[
                  { val: "featured",   label: "Featured" },
                  { val: "price-low",  label: "Price, low to high" },
                  { val: "price-high", label: "Price, high to low" },
                  { val: "date-new",   label: "Date, new to old" },
                ].map((opt) => (
                  <FormControlLabel
                    key={opt.val}
                    value={opt.val}
                    sx={{ alignItems: "center", mr: 0, mb: 1 }}
                    control={<Radio size="small" sx={{ color: "#999", "&.Mui-checked": { color: "#000" }, py: 0.5 }} />}
                    label={<Typography sx={{ fontSize: "15px", whiteSpace: "normal", lineHeight: 1.3 }}>{opt.label}</Typography>}
                  />
                ))}
              </Box>
            </RadioGroup>
          </FormControl>
        </Collapse>
      </Box>

      {/* Fixed Apply Button */}
      <Box sx={{ px: 3, py: 2 }}>
        <Button
          fullWidth
          onClick={onClose}
          sx={{
            backgroundColor: "#000",
            color: "#fff",
            borderRadius: 0,
            py: 1.8,
            fontSize: "15px",
            letterSpacing: "1.5px",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#222" },
          }}
        >
          APPLY
        </Button>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
